import React from "react";

/**
 * Hook to detect if an element's content is overflowing
 * @returns [refCallback, isOverflowing] - A callback ref to attach to any element and a boolean indicating overflow state
 */
export function useOverflowDetection(): [
  (element: HTMLElement | null) => void,
  boolean,
] {
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const refCallback = React.useCallback((node: HTMLElement | null) => {
    if (node) {
      setElement(node);
    }
  }, []);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (!element) {
      return;
    }

    const measure = () => {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    };

    // (Craig, 03-10-2025): This timeout allows the component to render first
    // before checking for overflow
    const timeoutId = window.setTimeout(measure, 100);

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => {
        window.clearTimeout(timeoutId);
        window.removeEventListener("resize", measure);
      };
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [element]);

  return [refCallback, isOverflowing];
}
